import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { Loader } from "@/components/ui/Loader";
import { ROUTES } from "@/config/constants";
import { formatDateBr, formatPct } from "@/lib/gente-gestao/format";
import { createPesquisaApi, extractGgError, fetchPesquisas, type GgPesquisaListDto } from "@/services/genteGestao";

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "ATIVA"
      ? "bg-emerald-50 text-emerald-700"
      : status === "ENCERRADA"
        ? "bg-slate-100 text-slate-600"
        : "bg-amber-50 text-amber-700";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>
      {status === "ATIVA" ? "Ativa" : status === "ENCERRADA" ? "Encerrada" : "Rascunho"}
    </span>
  );
}

export function GenteGestaoNpsListPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<GgPesquisaListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setItems(await fetchPesquisas());
    } catch (e) {
      setError(extractGgError(e, "Não foi possível carregar as pesquisas."));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setOpen(true)}>
          Nova pesquisa
        </Button>
      </div>
      {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      <div className="overflow-auto rounded-xl border border-slate-200 bg-white shadow-card">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader label="Carregando pesquisas..." />
          </div>
        ) : items.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-slate-500">Nenhuma pesquisa cadastrada.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Título</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Período</th>
                <th className="px-4 py-3">Convidados</th>
                <th className="px-4 py-3">Respostas</th>
                <th className="px-4 py-3">Adesão</th>
                <th className="px-4 py-3">eNPS</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium text-slate-800">{p.titulo}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={p.status} />
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {formatDateBr(p.dataInicio)} – {formatDateBr(p.dataFim)}
                  </td>
                  <td className="px-4 py-2">{p.convidados}</td>
                  <td className="px-4 py-2">{p.respostas}</td>
                  <td className="px-4 py-2">{formatPct(p.adesao, 0)}</td>
                  <td className="px-4 py-2 font-semibold">{p.enps == null ? "—" : p.enps}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      className="text-xs font-semibold text-brand-700 hover:underline"
                      onClick={() => navigate(`${ROUTES.GENTE_GESTAO_NPS}/${p.id}`)}
                    >
                      Abrir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <ModalNovaPesquisa
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(id) => {
          setOpen(false);
          navigate(`${ROUTES.GENTE_GESTAO_NPS}/${id}`);
        }}
      />
    </div>
  );
}

function ModalNovaPesquisa({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [titulo, setTitulo] = useState("");
  const [nomeEmpresa, setNomeEmpresa] = useState("SEMOG Engenharia");
  const [descricao, setDescricao] = useState("");
  const [modelo, setModelo] = useState<"completo" | "rapido" | "branco">("completo");
  const [linkGeral, setLinkGeral] = useState(true);
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitulo("");
      setDescricao("");
      setModelo("completo");
      setLinkGeral(true);
      setError(null);
    }
  }, [open]);

  async function save() {
    if (!titulo.trim() || !nomeEmpresa.trim()) {
      setError("Título e nome da empresa são obrigatórios.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await createPesquisaApi({
        titulo: titulo.trim(),
        nomeEmpresa: nomeEmpresa.trim(),
        descricao: descricao.trim(),
        modelo,
        modoPrivacidade: "CONFIDENCIAL",
        permiteLinkGeral: linkGeral,
        dataInicio: dataInicio || undefined,
        dataFim: dataFim || undefined,
      });
      onCreated(created.pesquisa.id);
    } catch (e) {
      setError(extractGgError(e, "Não foi possível criar a pesquisa."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nova pesquisa eNPS"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void save()} loading={saving}>
            Criar
          </Button>
        </div>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Input label="Título" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
        </div>
        <Input label="Nome da empresa" value={nomeEmpresa} onChange={(e) => setNomeEmpresa(e.target.value)} />
        <Select
          label="Modelo"
          value={modelo}
          onChange={(e) => setModelo(e.target.value as "completo" | "rapido" | "branco")}
          options={[
            { value: "completo", label: "eNPS Completo" },
            { value: "rapido", label: "eNPS Rápido" },
            { value: "branco", label: "Em branco" },
          ]}
        />
        <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
          <input type="checkbox" checked={linkGeral} onChange={(e) => setLinkGeral(e.target.checked)} />
          Permitir link geral — anônimo, sem nome
        </label>
        <Input label="Início" type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
        <Input label="Fim" type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Descrição</label>
          <textarea
            className="mt-1.5 min-h-[80px] w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
          />
        </div>
        {error && <p className="sm:col-span-2 text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
